import cls from "classnames"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import {
    getCharaAvatar,
    getSpeakerDecorLabel,
} from "@/components/DialogBox/const"
import Toast from "@/components/Toast"
import { galleryItems } from "@/data/gallery"
import { drawQRCode, useShareChannels } from "@/hooks"
import { type I18nTextPath, i18n } from "@/i18n"
import { useModalStore } from "@/store"
import { useRoleInfo } from "@/user/session"
import { useLottery } from "../Lottery"
import { CustomModal } from "../Modal"
import ShareButton from "../ShareButton"
import StrokeText from "../StrokeText"
import { px2rem } from "@/utils/adaptFont"
import { computeDialogFontSize, drawShareImage } from "./helper"

import styles from "./index.module.scss"

const SHARE_DIALOG_TEXT =
    "对话框能字数最多为40个字对话框能字数最多为40个字对话框能字数最多为40个字啊"

export const ShareModal: React.FC = () => {
    const open = useModalStore((state) => state.shareOpen)
    const onCancel = useModalStore((state) => state.closeShare)
    const galleryId = useModalStore((state) => state.galleryId)
    const eventImage = useModalStore((state) => state.eventImage)
    const storeDialogText = useModalStore((state) => state.dialogText)
    const hideDialog = useModalStore((state) => state.hideDialog)
    const galleryItem = useMemo(() => {
        return galleryItems.find((item) => item.id === galleryId)
    }, [galleryId])

    const speaker = useModalStore((state) => state.speaker)
    const expression = useModalStore((state) => state.expression)

    const shareImage = galleryItem?.image ?? eventImage
    const dialogText = storeDialogText || SHARE_DIALOG_TEXT

    const charAvatar = speaker ? getCharaAvatar(speaker, expression) : undefined
    const displayAppellation = speaker
        ? getSpeakerDecorLabel(speaker)
        : i18n.get("char.char_472_pasngr.appellation")
    const displayName = speaker
        ? i18n.get(speaker as never)
        : i18n.get("char.char_472_pasngr.name")

    const user = useRoleInfo()
    const [qrCodeUrl, setQrCodeUrl] = useState("")
    const [imageSrc, setImageSrc] = useState("")
    const imageRef = useRef<HTMLImageElement>(null)
    const shareTips = i18n.getArray("map.gallery.shareTips")
    const dialogFontSize = useMemo(
        () => computeDialogFontSize(dialogText),
        [dialogText],
    )

    const generateQRCode = useCallback(async () => {
        if (!open) return

        const qrCanvas = await drawQRCode(user?.uid, false)
        setQrCodeUrl(qrCanvas.toDataURL("image/png"))
    }, [user?.uid, open])

    const generateShareImage = useCallback(async () => {
        if (!shareImage || !qrCodeUrl) return

        const { base64 } = await drawShareImage({
            galleryImage: shareImage,
            qrCodeUrl,
            tips: shareTips,
            appellation: displayAppellation,
            name: displayName,
            dialogText,
            hideDialog,
            charImage: charAvatar?.src,
        })
        setImageSrc(base64)
    }, [
        shareImage,
        shareTips,
        displayAppellation,
        displayName,
        qrCodeUrl,
        dialogText,
        hideDialog,
        charAvatar?.src,
    ])

    const download = useCallback(async () => {
        if (!imageSrc) {
            Toast.message("download error")
            return
        }

        try {
            const link = document.createElement("a")
            link.href = imageSrc
            link.download = `${i18n.get("share.title")}.png`
            document.body.append(link)
            link.click()
            link.remove()
        } catch {
            Toast.message("download error")
        }
    }, [imageSrc])

    const lottery = useLottery()
    const hasShowLottery = useRef(false)

    useEffect(() => {
        if (!open) hasShowLottery.current = false
    }, [open])

    const handleFirstMainShare = useCallback(() => {
        if (hasShowLottery.current) return
        hasShowLottery.current = true
        onCancel()
        lottery?.start()
    }, [lottery, onCancel])

    const { channels, onClickChannel } = useShareChannels({
        download,
        isRecall: false,
        imageRef,
        imageSrc,
        onFirstMainShare: handleFirstMainShare,
    })

    useEffect(() => {
        generateQRCode()
    }, [generateQRCode])

    useEffect(() => {
        generateShareImage()
    }, [generateShareImage])

    const handleCancel = useCallback(() => {
        onCancel()
    }, [onCancel])

    return (
        <>
        <CustomModal
            open={open}
            onCancel={handleCancel}
            showCancelBtn={false}
            showOkBtn={false}
            showClose={false}
            maskClosable
            contentClassName={styles.share}
        >
            <div className={styles.main}>
                {shareImage && (
                    <div className={styles.cg}>
                        <img
                            src={shareImage}
                            alt={
                                galleryItem
                                    ? i18n.get(
                                          galleryItem.title as I18nTextPath,
                                      )
                                    : ""
                            }
                        />
                    </div>
                )}
                <div className={styles.tips}>
                    {shareTips.map((text, i) => (
                        <span
                            key={i}
                            className={cls(
                                styles.text,
                                i === 0 && styles.highlight,
                            )}
                        >
                            {text}
                        </span>
                    ))}
                </div>
            </div>
            <div className={styles.side}>
                <div
                    className={styles.charDialog}
                    style={hideDialog ? { visibility: "hidden" } : undefined}
                >
                    {charAvatar && (
                        <img
                            className={styles.char}
                            src={charAvatar.src}
                            alt=""
                            draggable={false}
                        />
                    )}
                    <div className={styles.dialog}>
                        <div className={styles.charName}>
                            <StrokeText
                                text={displayAppellation}
                                fontFamily="Trajan-Bold"
                                fontSize={11}
                                fillColor="#443122"
                                inner={{
                                    color: "#ffffff",
                                    width: 2,
                                }}
                                outer={{
                                    color: "#a9885b",
                                    width: 2,
                                }}
                                offsetY={0}
                                padding={4}
                                className={styles.appellation}
                            />
                            <StrokeText
                                text={displayName}
                                fontFamily="a한글세상B"
                                fontSize={28}
                                fillColor="#443122"
                                inner={{
                                    color: "#ffffff",
                                    width: 3,
                                }}
                                outer={{
                                    color: "#f6d1aa",
                                    width: 3,
                                }}
                                offsetY={0}
                                padding={6}
                                className={styles.name}
                            />
                        </div>
                        <div
                            className={styles.text}
                            style={dialogFontSize !== 21 ? { fontSize: px2rem(dialogFontSize) } : undefined}
                        >
                            {dialogText}
                        </div>
                    </div>
                </div>
                <div className={styles.qrcode}>
                    <img src={qrCodeUrl} alt="qrcode" />
                </div>
            </div>
            {imageSrc && (
                <img
                    ref={imageRef}
                    src={imageSrc}
                    alt={i18n.get("share.title")}
                    decoding="sync"
                    loading="eager"
                    className={styles.shareImage}
                />
            )}
            <div className={styles.shareButtons}>
                {channels.map((key) => (
                    <ShareButton
                        key={key}
                        type={key}
                        onClick={() => onClickChannel(key)}
                    />
                ))}
            </div>
        </CustomModal>
        {lottery?.modal}
        </>
    )
}
